export type TimelineEventForStudio = {
  id?: string
  eventType?: string
  date: string
  title: string
  description: string
  price?: number
  tag?: string
  metadata?: unknown
  autoGenerated?: boolean
}

export type StudioHistoryRow =
  | { kind: 'event'; event: TimelineEventForStudio; key: string }
  | {
      kind: 'sl_bundle'
      count: number
      from: string
      to: string
      stopPrice: number
      lastClose: number | null
      events: TimelineEventForStudio[]
      key: string
    }
  | {
      kind: 'notify_bundle'
      groupKey: string
      labelKey: string
      count: number
      from: string
      to: string
      events: TimelineEventForStudio[]
      key: string
    }

const MANUAL_EVENT_TYPES = new Set([
  'entry',
  'add',
  'scale_in',
  'reduce_size',
  'scale_out',
  'exit',
  'full_exit',
  'stop_move',
])

export function isSlAlertTimelineEvent(event: TimelineEventForStudio) {
  const eventType = (event.eventType ?? '').toLowerCase()
  if (eventType.includes('stop') && eventType.includes('alert')) return true
  if (eventType === 'below_sl' || eventType === 'approaching_sl' || eventType === 'stop_loss') return true
  const metadata = event.metadata as { alert_type?: string } | undefined
  if (metadata?.alert_type === 'below_sl' || metadata?.alert_type === 'approaching_sl') return true
  const title = (event.title ?? '').toLowerCase()
  const desc = (event.description ?? '').toLowerCase()
  if (title.includes('stop loss alert') || title.includes('stop-loss alert')) return true
  if (desc.includes('stop loss reached') || desc.includes('approaching stop loss')) return true
  return false
}

function notifyBundleLabelKey(groupKey: string): string {
  const type = groupKey.replace(/^auto:/, '')
  switch (type) {
    case 'tp_hit':
      return 'tpHit'
    case 'sl_hit':
      return 'slHit'
    case 'market_data':
      return 'marketData'
    case 'review':
      return 'review'
    default:
      return 'generic'
  }
}

function getNotificationGroupKey(event: TimelineEventForStudio): string | null {
  if (isSlAlertTimelineEvent(event)) return '__sl__'
  const eventType = (event.eventType ?? '').toLowerCase()
  if (MANUAL_EVENT_TYPES.has(eventType)) return null
  if (event.autoGenerated) return `auto:${eventType || 'system'}`
  if (eventType === 'tp_hit' || eventType === 'sl_hit') return `auto:${eventType}`
  return null
}

export function buildStudioHistoryRows(
  events: TimelineEventForStudio[],
  options: { stopPrice: number; formatShortDate: (date: string) => string }
): StudioHistoryRow[] {
  const rows: StudioHistoryRow[] = []
  let buffer: TimelineEventForStudio[] = []
  let bufferKey: string | null = null

  const flushBuffer = () => {
    if (!buffer.length || !bufferKey) {
      buffer = []
      bufferKey = null
      return
    }

    const from = options.formatShortDate(buffer[0].date)
    const to = options.formatShortDate(buffer[buffer.length - 1].date)
    const key = `${bufferKey}-${buffer[0].id ?? buffer[0].date}`

    if (bufferKey === '__sl__') {
      rows.push({
        kind: 'sl_bundle',
        count: buffer.length,
        from,
        to,
        stopPrice: options.stopPrice,
        lastClose: buffer[buffer.length - 1].price ?? null,
        events: [...buffer],
        key,
      })
    } else if (buffer.length > 1) {
      rows.push({
        kind: 'notify_bundle',
        groupKey: bufferKey,
        labelKey: notifyBundleLabelKey(bufferKey),
        count: buffer.length,
        from,
        to,
        events: [...buffer],
        key,
      })
    } else {
      const only = buffer[0]
      rows.push({
        kind: 'event',
        event: only,
        key: only.id ?? `${only.date}-${only.eventType ?? only.title}`,
      })
    }

    buffer = []
    bufferKey = null
  }

  for (const event of events) {
    const groupKey = getNotificationGroupKey(event)
    if (!groupKey) {
      flushBuffer()
      rows.push({
        kind: 'event',
        event,
        key: event.id ?? `${event.date}-${event.eventType ?? event.title}`,
      })
      continue
    }

    if (groupKey === bufferKey) {
      buffer.push(event)
      continue
    }

    flushBuffer()
    bufferKey = groupKey
    buffer = [event]
  }

  flushBuffer()
  return rows
}
